import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	UnreadNotifications = 'bizy-unread-notifications',
}

export const bizyUnreadNotificationsQueryKey = () => [QueryKey.UnreadNotifications];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyUnreadNotificationsPath = (prefix = '') => `${prefix}/bizy/notifications/unread`;
export const bizyMarkNotificationsSeenPath = (prefix = '') => `${prefix}/bizy/notifications/seen`;

// Cooldown for the unread polling. The query refetches on window focus, so
// without a staleTime a user tabbing across browsers fans out into N requests
// in the same second. 30s is conservative — anything new lands within that
// window via the next focus event after the cooldown expires.
const UNREAD_STALE_TIME_MS = 30_000;

// ─── API response types ──────────────────────────────────────────────────────

// Response keys are camelCase because the shared `headers` helper adds the
// `Key-Inflection: camel` middleware header. Backend sends snake_case; the
// HTTP client transforms to camel before reaching us.
// Discriminates what the latest unread notification points at. Post-BIZY-633,
// an `observed_moment` notification has no conversation to open — it opens the
// People Browser instead. `null` when there is nothing unread.
export type BizyLatestUnreadKind = 'conversation' | 'observed_moment' | null;

export interface BizyUnreadNotificationsResponse {
	hasUnread: boolean;
	unreadCount: number;
	// Null when the latest unread is an observed moment (nothing to open).
	latestConversationId: string | null;
	// Added in BIZY-633. Older backends omit these; treat missing as a plain
	// conversation peek so behavior is unchanged until the BE lands.
	latestUnreadKind?: BizyLatestUnreadKind;
	latestUnreadPersonId?: string | null;
}

interface BizyMarkSeenResponse {
	markedCount: number;
}

interface BizyMarkSeenArgs {
	/**
	 * Conversation id to scope the mark-seen to. When provided, only
	 * notifications linked to that conversation are flipped to opened —
	 * other unread notifications stay visible so the user can still get to
	 * them. Omit to mark all unopened (used by explicit "clear" affordances).
	 */
	conversationId?: string;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches whether the current user has unread proactive Bizy notifications.
 * Drives the navbar Ask Bizy button's waving state + dot, and the chat
 * panel jumping to the proactive conversation on open.
 *
 * Refetches on window focus so a notification delivered while the user is on
 * the page surfaces the peek without a hard reload. A staleTime cooldown
 * dedupes bursts of focus events across tabs.
 *
 * Disabled for users without Bizy access — there's nothing to show them, so
 * we skip the round trip entirely. Downstream callers (`useHasBizyNotification`,
 * `useUnreadBizyConversationId`) cascade the disabled state to a `false` /
 * `null` return.
 *
 * GET /api/v2/bizy/notifications/unread
 */
export const useBizyUnreadNotifications = () => {
	const http = useHttpClient();
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	return useQuery({
		queryKey: bizyUnreadNotificationsQueryKey(),
		queryFn: async (): Promise<BizyUnreadNotificationsResponse> => {
			const res = await http.get<BizyUnreadNotificationsResponse>(bizyUnreadNotificationsPath(), { headers });
			return res.data;
		},
		enabled: isBizyEnabledForUser,
		refetchOnWindowFocus: true,
		staleTime: UNREAD_STALE_TIME_MS,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Marks the current user's proactive notifications as seen. Pass a
 * `conversationId` to scope the mark to only the notification(s) linked to
 * that conversation — the chat-view coordinator does this so older unread
 * notifications for other conversations stay visible.
 *
 * POST /api/v2/bizy/notifications/seen
 */
export const useBizyMarkNotificationsSeenMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ conversationId }: BizyMarkSeenArgs = {}): Promise<BizyMarkSeenResponse> => {
			const body = conversationId ? { conversation_id: conversationId } : {};
			const res = await http.post<BizyMarkSeenResponse>(bizyMarkNotificationsSeenPath(), body, { headers });
			return res.data;
		},
		onSuccess: (data, vars) => {
			// Optimistically update so the indicator disappears immediately —
			// avoids a one-frame stale state while the next refetch lands.
			if (data.markedCount === 0) return;

			const markedConversationId = vars?.conversationId;
			let remaining = 0;
			queryClient.setQueryData<BizyUnreadNotificationsResponse>(bizyUnreadNotificationsQueryKey(), (prev) => {
				if (!prev) return prev;
				remaining = Math.max(0, prev.unreadCount - data.markedCount);
				// If we just marked the same conversation that's currently
				// surfaced as latest, clear it so consumers don't briefly
				// re-engage with an already-handled conversation. The refetch
				// below rolls it forward to whichever is now on top.
				const justClearedLatest = markedConversationId !== undefined && markedConversationId === prev.latestConversationId;
				// Spread `prev` so newer fields (latestUnreadKind,
				// latestUnreadPersonId — BIZY-633) survive the optimistic
				// window; without this they'd vanish until the refetch lands and
				// a remaining observed-moment unread could momentarily look like
				// a legacy null-conversation notification.
				return {
					...prev,
					hasUnread: remaining > 0,
					unreadCount: remaining,
					latestConversationId: remaining > 0 && !justClearedLatest ? prev.latestConversationId : null,
				};
			});

			// If anything remains, force a refetch so latest_conversation_id
			// rolls forward to whichever notification is now on top. When
			// nothing remains the optimistic update is already correct, so
			// skip the wasted /unread call.
			if (remaining > 0) {
				queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
			}
		},
	});
};
